import { createDirectoryHandleFs } from "./directoryHandleFs.js";
import { GIT_GITDIR, GIT_WORKDIR } from "./gitConstants.js";
import { inferLanguageModeFromFilename } from "../../doc/inferLanguageFromFilename.js";
import { monacoLanguageId } from "../../editor/monacoLanguageId.js";
import type { DiffEditorSnapshot } from "../../ui/diffTabMonaco.js";
import type { GitTerminalRunResult } from "./gitTerminalShell.js";
import { GIT_DIFF_MAX_SNAPSHOTS } from "./gitWorkspaceDiff.js";

function decodeUtf8Loose(bytes: Uint8Array | undefined | null): string {
  if (!bytes || bytes.length === 0) return "";
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
}

function formatCommitTimestamp(timestamp: number, timezoneOffset: number): string {
  const date = new Date((timestamp - timezoneOffset * 60) * 1000);
  return date.toUTCString().replace("GMT", `${timezoneOffset <= 0 ? "+" : "-"}${String(Math.abs(timezoneOffset) / 60).padStart(2, "0")}00`);
}

function formatCommitHeader(oid: string, commit: {
  message: string;
  author: { name: string; email: string; timestamp: number; timezoneOffset: number };
  parent: string[];
}): string {
  const lines: string[] = [];
  lines.push(`commit ${oid}`);
  lines.push(`Author: ${commit.author.name} <${commit.author.email}>`);
  lines.push(`Date:   ${formatCommitTimestamp(commit.author.timestamp, commit.author.timezoneOffset)}`);
  lines.push("");
  for (const l of commit.message.trimEnd().split("\n")) {
    lines.push(`    ${l}`);
  }
  lines.push("");
  return lines.join("\n");
}

/**
 * Resolve a git ref expression to a full commit OID.
 * Handles: HEAD, branch/tag names, full SHAs, abbreviated SHAs, HEAD~N, HEAD^, ref~N.
 */
export async function resolveCommitOid(
  root: FileSystemDirectoryHandle,
  refExpr: string,
): Promise<string> {
  const fs = createDirectoryHandleFs(root);
  const git = await import("isomorphic-git");

  // HEAD~N or HEAD^N or ref~N syntax (not natively supported by isomorphic-git resolveRef).
  const tildeMatch = refExpr.match(/^(.+?)(?:~(\d+)|(\^+))$/);
  if (tildeMatch) {
    const base = tildeMatch[1]!;
    const n = tildeMatch[2] !== undefined ? parseInt(tildeMatch[2], 10) : tildeMatch[3]!.length;
    let oid = await resolveCommitOid(root, base);
    for (let i = 0; i < n; i++) {
      const { commit } = await git.readCommit({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, oid });
      const parent = commit.parent[0];
      if (!parent) throw new Error(`Commit ${oid.slice(0, 7)} has no parent (reached root commit).`);
      oid = parent;
    }
    return oid;
  }

  // Try resolveRef (covers HEAD, branches, tags, full SHAs).
  try {
    return await git.resolveRef({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, ref: refExpr });
  } catch {
    // Fall back to expandOid for abbreviated SHAs like "abc1234".
    return await git.expandOid({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, oid: refExpr });
  }
}

async function readBlobAtCommit(
  root: FileSystemDirectoryHandle,
  commitOid: string,
  filepath: string,
): Promise<string> {
  const fs = createDirectoryHandleFs(root);
  const git = await import("isomorphic-git");
  try {
    const { blob } = await git.readBlob({
      fs,
      dir: GIT_WORKDIR,
      gitdir: GIT_GITDIR,
      oid: commitOid,
      filepath,
    });
    return decodeUtf8Loose(blob);
  } catch {
    return "";
  }
}

/** Collect files that differ between parentOid and commitOid trees. */
async function collectChangedPaths(
  root: FileSystemDirectoryHandle,
  parentOid: string,
  commitOid: string,
): Promise<string[]> {
  const fs = createDirectoryHandleFs(root);
  const git = await import("isomorphic-git");
  const changed: string[] = [];
  await git.walk({
    fs,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    trees: [git.TREE({ ref: parentOid }), git.TREE({ ref: commitOid })],
    map: async (filepath, [parentEntry, commitEntry]) => {
      const pt = parentEntry ? await parentEntry.type() : null;
      const ct = commitEntry ? await commitEntry.type() : null;
      if (pt === "tree" || ct === "tree") return;
      const po = parentEntry ? await parentEntry.oid() : null;
      const co = commitEntry ? await commitEntry.oid() : null;
      if (po !== co) changed.push(filepath);
      return null;
    },
  });
  return changed;
}

function snapshotForCommitPair(
  filepath: string,
  leftOid: string,
  rightOid: string,
  leftText: string,
  rightText: string,
): DiffEditorSnapshot {
  const base = filepath.split("/").pop() ?? filepath;
  const lang = inferLanguageModeFromFilename(base);
  return {
    leftLabel: `${leftOid.slice(0, 7)} — ${filepath}`,
    rightLabel: `${rightOid.slice(0, 7)} — ${filepath}`,
    leftText,
    rightText,
    monacoLanguage: monacoLanguageId(lang),
    highlightLanguage: lang,
  };
}

/** Parse `git show` arguments. Returns `null` for unsupported flags. */
export function parseGitShowArgs(rest: string[]): {
  ref: string;
  filepath: string | null;
} | null {
  // Filter benign flags.
  const positional = rest.filter((a) => {
    if (a === "--no-color" || a === "--color=never" || a === "--color=false") return false;
    if (a === "--stat" || a.startsWith("--stat=") || a === "--name-only" || a === "--name-status") return false;
    if (a.startsWith("-") && a.length > 1) return false;
    return true;
  });

  if (positional.length === 0) {
    return { ref: "HEAD", filepath: null };
  }

  const expr = positional[0]!;

  // <ref>:<path> syntax.
  const colonIdx = expr.indexOf(":");
  if (colonIdx >= 0) {
    return {
      ref: expr.slice(0, colonIdx) || "HEAD",
      filepath: expr.slice(colonIdx + 1) || null,
    };
  }

  return { ref: expr, filepath: null };
}

/**
 * Implements `git show` for the in-app Git terminal.
 * - `git show <ref>:<path>` → opens the file content at that commit as a new text tab.
 * - `git show [<ref>]` → prints commit info and offers compare tabs for changed files.
 */
export async function runGitWorkspaceShow(
  root: FileSystemDirectoryHandle,
  rest: string[],
): Promise<GitTerminalRunResult> {
  const parsed = parseGitShowArgs(rest);
  if (!parsed) {
    return { stdout: "", stderr: "Unsupported git show arguments.", exitCode: 1 };
  }

  const { ref, filepath } = parsed;

  // Resolve the commit OID.
  let commitOid: string;
  try {
    commitOid = await resolveCommitOid(root, ref);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { stdout: "", stderr: `Cannot resolve ref "${ref}": ${msg}`, exitCode: 1 };
  }

  // ── git show <ref>:<path> ── open file content at that commit as a text tab.
  if (filepath !== null) {
    const fs = createDirectoryHandleFs(root);
    const git = await import("isomorphic-git");
    let blob: Uint8Array;
    try {
      const result = await git.readBlob({
        fs,
        dir: GIT_WORKDIR,
        gitdir: GIT_GITDIR,
        oid: commitOid,
        filepath,
      });
      blob = result.blob;
    } catch {
      return {
        stdout: "",
        stderr: `File not found at ${ref}: ${filepath}`,
        exitCode: 1,
      };
    }
    const content = decodeUtf8Loose(blob);
    const shortSha = commitOid.slice(0, 7);
    const basename = filepath.split("/").pop() ?? filepath;
    const title = `${basename} @ ${shortSha}`;
    const lang = inferLanguageModeFromFilename(basename);
    return {
      stdout: `Opened "${title}" as a new tab.\n`,
      exitCode: 0,
      openTextTab: { title, content, language: lang },
    };
  }

  // ── git show [<ref>] ── print commit info + offer compare tabs.
  const fs = createDirectoryHandleFs(root);
  const git = await import("isomorphic-git");
  let commit: Awaited<ReturnType<typeof git.readCommit>>["commit"];
  try {
    ({ commit } = await git.readCommit({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, oid: commitOid }));
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { stdout: "", stderr: `Could not read commit ${commitOid.slice(0, 7)}: ${msg}`, exitCode: 1 };
  }

  const header = formatCommitHeader(commitOid, commit);

  // Initial commit has no parent — nothing to compare.
  const parentOid = commit.parent[0];
  if (!parentOid) {
    return {
      stdout: header + "(initial commit — no parent to diff against)\n",
      exitCode: 0,
    };
  }

  // Collect files changed in this commit.
  let changedPaths: string[];
  try {
    changedPaths = await collectChangedPaths(root, parentOid, commitOid);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { stdout: header, stderr: `Could not read commit diff: ${msg}`, exitCode: 1 };
  }

  if (changedPaths.length === 0) {
    return { stdout: header + "No file changes in this commit.\n", exitCode: 0 };
  }

  // Build diff snapshots (capped).
  const targetPaths = changedPaths.slice(0, GIT_DIFF_MAX_SNAPSHOTS);
  const snapshots: DiffEditorSnapshot[] = [];
  for (const p of targetPaths) {
    const [leftText, rightText] = await Promise.all([
      readBlobAtCommit(root, parentOid, p),
      readBlobAtCommit(root, commitOid, p),
    ]);
    snapshots.push(snapshotForCommitPair(p, parentOid, commitOid, leftText, rightText));
  }

  const truncatedNote =
    changedPaths.length > GIT_DIFF_MAX_SNAPSHOTS
      ? ` (showing ${GIT_DIFF_MAX_SNAPSHOTS} of ${changedPaths.length} changed files)`
      : undefined;

  return {
    stdout: header,
    exitCode: 0,
    openDiffSnapshots: snapshots,
    openDiffSnapshotsTotalCount: changedPaths.length,
    openDiffSnapshotsTruncatedNote: truncatedNote,
  };
}
